import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { api, useAuth, formatApiError } from "../lib/api";
import { landingFor } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UserPlus, CircleNotch, CheckCircle } from "@phosphor-icons/react";

/**
 * Invite Activation / Signup page.
 * Reached via the emailed "Activate Account" link: /signup?email=<invited_email>
 * - Email is pre-filled and read-only (they must sign up with the invited address)
 * - Prompts user to CREATE a password (not enter existing one)
 * - Then 2FA code → account is active
 */
export default function Signup() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invitedEmail = (params.get("email") || "").toLowerCase().trim();

  const [step, setStep] = useState("create");  // create | code
  const [name, setName] = useState("");
  const [email] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notInvited, setNotInvited] = useState(false);

  useEffect(() => {
    if (!invitedEmail) setNotInvited(true);
  }, [invitedEmail]);

  if (!loading && user) return <Navigate to={landingFor(user.role)} replace />;

  const submitCreate = async (e) => {
    e?.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/signup", { email, password, name: name || email.split("@")[0] });
      setDevCode(res.data.dev_code || null);
      setStep("code");
      toast.success("Account created — check your email for a 6-digit code");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403) {
        setError("This email is not authorized to sign up. Ask your admin to invite you again.");
      } else if (typeof detail === "string" && detail.toLowerCase().includes("already")) {
        setError("An account already exists for this email. Please sign in instead.");
      } else {
        setError(formatApiError(detail, "Sign-up failed. Please try again."));
      }
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true); setError("");
    try {
      const res = await api.post("/auth/verify", { email, code });
      setUser(res.data.user);
      toast.success("Account activated");
      nav(landingFor(res.data.user.role), { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Invalid or expired code."));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/resend-code", { email, password });
      setDevCode(res.data.dev_code || null);
      toast.success("New code sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not resend code"));
    } finally { setBusy(false); }
  };

  if (notInvited) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
          <div className="text-sm font-semibold text-[#0F1E38]">Invalid activation link</div>
          <div className="text-xs text-slate-500 mt-2">
            This link is missing the required invite information. Please use the exact link from your invitation email, or ask your admin to re-send the invite.
          </div>
          <Button data-testid="signup-goto-login" onClick={() => nav("/login")} className="w-full mt-6 h-11 bg-[#0F1E38] hover:bg-[#1E3A5F] text-white">
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
            <UserPlus size={17} className="text-[#B7791F]" weight="bold" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[#0F1E38]">Activate your account</div>
            <div className="text-[11px] text-slate-500">SoverenX Command Suite</div>
          </div>
        </div>

        <div className="text-[11px] text-slate-500 mt-4 mb-5 leading-relaxed bg-slate-50 border border-slate-200 rounded p-2.5">
          You were invited to join as a team member. Create a password below to activate your account.
        </div>

        {step === "create" && (
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Your email (from invite)</Label>
              <Input
                data-testid="signup-email"
                type="email"
                value={email}
                readOnly
                disabled
                className="mt-1 h-11 bg-slate-50 text-slate-600 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Your name <span className="text-slate-400">(optional)</span></Label>
              <Input
                data-testid="signup-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alex Chen"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Create password</Label>
              <Input
                data-testid="signup-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11"
                required
                minLength={8}
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Confirm password</Label>
              <Input
                data-testid="signup-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 h-11"
                required
                minLength={8}
              />
            </div>
            {error && <div data-testid="signup-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button
              data-testid="signup-submit"
              type="submit"
              disabled={busy || !password || !confirm}
              className="w-full h-11 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Create account"}
            </Button>
            <div className="text-center">
              <button type="button" onClick={() => nav("/login")} className="text-[11px] text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>
                Already activated? Sign in →
              </button>
            </div>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={submitCode} className="space-y-4">
            <div className="flex items-center gap-2 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2.5">
              <CheckCircle size={16} weight="fill" />
              <span>Account created. Enter the code we emailed you.</span>
            </div>
            <div>
              <div className="text-sm text-[#0F1E38] font-medium">Verification code</div>
              <div className="text-xs text-slate-500 mt-1">
                Sent to <span className="text-[#0F1E38] font-mono">{email}</span>
              </div>
            </div>
            {devCode && (
              <div className="text-[11px] font-mono bg-amber-50 border border-amber-200 text-amber-900 p-2 rounded">
                <span className="font-semibold">DEV MODE</span> — code: <span className="font-bold">{devCode}</span>
              </div>
            )}
            <Input
              data-testid="signup-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="h-14 text-center text-2xl font-mono tracking-[0.4em]"
              autoFocus
              placeholder="——————"
            />
            {error && <div data-testid="signup-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button
              data-testid="signup-verify"
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full h-11 bg-[#0F1E38] hover:bg-[#1E3A5F] text-white font-medium"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Activate & Sign in"}
            </Button>
            <div className="flex justify-between text-[11px] pt-1">
              <button type="button" onClick={() => setStep("create")} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>← Back</button>
              <button type="button" onClick={resend} disabled={busy} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>Resend code</button>
            </div>
          </form>
        )}

        <div className="mt-8 pt-4 border-t border-slate-100 text-[10px] text-slate-400 text-center font-mono uppercase tracking-wider">
          SoverenX · Invited access
        </div>
      </div>
    </div>
  );
}
